"use client";

import type { ProcessedSet, ReviewData } from "@workspace/types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRealtimeSetReview } from "@/hooks/features/festival/use-realtime-set-review";
import { trackSetInteraction } from "@/lib/services/analytics";
import { trpc } from "@/lib/trpc/react";
import { isEmptyReview } from "@workspace/shared-utils";
import { useCurrentUser } from "@/stores";

// Local review type for optimistic updates
interface Review {
	setId: string;
	artistId: string;
	rating: number;
	content: string | null;
	loved: boolean;
	sawLive: boolean;
	sawStream: boolean;
	wantToSee: boolean;
	mustSee: boolean;
}

// Use the shared ReviewData type instead of duplicate interface
type ReviewModalData = ReviewData;

interface UseFestivalReviewManagementProps {
	user: { id: string } | null;
	processedSets: ProcessedSet[];
	userSetReviews: Review[]; // Now accepts the clean camelCase format directly
	savedSchedule: string[];
	onAuthRequired: (message?: string) => void;
	setSavedSchedule: React.Dispatch<React.SetStateAction<string[]>>;
	festivalId?: string;
	refetchSetReviews?: () => void; // Server action refetch for consistency
	socialInteractionsLoaded?: boolean; // Flag to check if social interactions are loaded
}

interface UseFestivalReviewManagementReturn {
	// State
	reviews: Review[];
	reviewingSet: string | null;
	currentReviewData: ReviewModalData;
	managedSavedSchedule: string[]; // NEW: Hook manages its own schedule state

	// Actions
	handleToggleSetInSchedule: (setId: string) => void;
	handleStartReview: (setId: string) => void;
	handleCloseReviewModal: () => void;
	handleReviewChange: (field: string, value: string | number | boolean) => void;
	handleReviewSave: () => void;
}

/**
 * Hook for managing festival set reviews with optimistic updates
 *
 * Features:
 * - Single source of truth for review state
 * - Optimistic updates for instant UI feedback
 * - Handles both adds/updates and deletes separately
 * - Broadcasts changes to all components via CustomEvent
 * - Smart cleanup - only clears optimistic state when server confirms
 */
export function useFestivalReviewManagement({
	user,
	processedSets,
	userSetReviews,
	savedSchedule: _savedSchedule,
	onAuthRequired,
	setSavedSchedule,
	festivalId,
	refetchSetReviews,
	socialInteractionsLoaded = true, // Default to true for backward compatibility
}: UseFestivalReviewManagementProps): UseFestivalReviewManagementReturn {
	const currentUser = useCurrentUser();
	// Hook manages its own schedule state for immediate consistency
	const [managedSavedSchedule, setManagedSavedSchedule] = useState<string[]>(
		[],
	);

	// Initialize managed schedule from parent state
	useEffect(() => {
		setManagedSavedSchedule(_savedSchedule);
	}, [_savedSchedule]);

	// Memoize server reviews transformation
	const serverReviews = useMemo(() => {
		return (userSetReviews || []).map((r) => ({
			setId: r.setId || "",
			artistId: r.artistId || "",
			rating: r.rating || 0,
			content: r.content || null,
			loved: r.loved || false,
			sawLive: r.sawLive || false,
			sawStream: r.sawStream || false,
			wantToSee: r.wantToSee || false,
			mustSee: r.mustSee || false,
		}));
	}, [userSetReviews]);

	// Local state for optimistic updates - track both adds/updates and deletes
	const [optimisticReviews, setOptimisticReviews] = useState<Review[]>([]);
	const [optimisticDeletes, setOptimisticDeletes] = useState<string[]>([]);

	// Helper to apply optimistic updates consistently
	const applyOptimisticUpdate = useCallback(
		(setId: string, optimisticReview: Review | null) => {
			if (optimisticReview) {
				// Add/Update: Remove from deletes, add to reviews
				setOptimisticDeletes((prev) => prev.filter((id) => id !== setId));
				setOptimisticReviews((prev) => {
					const filtered = prev.filter((r) => r.setId !== setId);
					return [...filtered, optimisticReview];
				});
			} else {
				// Delete: Remove from reviews, add to deletes
				setOptimisticReviews((prev) => prev.filter((r) => r.setId !== setId));
				setOptimisticDeletes((prev) => [
					...prev.filter((id) => id !== setId),
					setId,
				]);
			}
		},
		[],
	);

	// Clear optimistic updates ONLY when confirmed by server
	useEffect(() => {
		// Smart cleanup: Only clear optimistic updates that have been confirmed
		setOptimisticDeletes((prev) => {
			// Remove from optimistic deletes if the item is no longer in server reviews
			return prev.filter((deletedSetId) => {
				const stillExistsOnServer = serverReviews.some(
					(sr) => sr.setId === deletedSetId,
				);
				// Keep in optimistic deletes if it still exists on server (delete not confirmed yet)
				return stillExistsOnServer;
			});
		});

		// For optimistic reviews (adds/updates), clear them once server has the same data
		setOptimisticReviews((prev) => {
			return prev.filter((optReview) => {
				const serverReview = serverReviews.find(
					(sr) => sr.setId === optReview.setId,
				);
				if (!serverReview) {
					// Server doesn't have it yet, keep optimistic
					return true;
				}
				// Check if server has caught up with our optimistic update
				const serverMatches =
					serverReview.rating === optReview.rating &&
					serverReview.content === optReview.content &&
					serverReview.loved === optReview.loved &&
					serverReview.sawLive === optReview.sawLive &&
					serverReview.sawStream === optReview.sawStream &&
					serverReview.wantToSee === optReview.wantToSee &&
					serverReview.mustSee === optReview.mustSee;

				// Remove from optimistic if server has caught up
				return !serverMatches;
			});
		});
	}, [serverReviews]);

	// Combine server reviews with optimistic updates
	const reviews = useMemo(() => {
		// Start with server reviews
		const reviewMap = new Map(serverReviews.map((r) => [r.setId, r]));

		// Remove optimistically deleted reviews
		for (const deletedSetId of optimisticDeletes) {
			reviewMap.delete(deletedSetId);
		}

		// Apply optimistic adds/updates
		for (const optimisticReview of optimisticReviews) {
			reviewMap.set(optimisticReview.setId, optimisticReview);
		}

		return Array.from(reviewMap.values());
	}, [serverReviews, optimisticReviews, optimisticDeletes]);

	const [reviewingSet, setReviewingSet] = useState<string | null>(null);

	// Review modal state
	const [currentReviewData, setCurrentReviewData] = useState<ReviewModalData>({
		rating: 0,
		content: "",
		loved: false,
		sawLive: false,
		sawStream: false,
		wantToSee: false,
		mustSee: false,
	});

	// Use tRPC mutation directly - real-time will handle the updates
	const saveSetReviewMutation = trpc.festival.saveSetReview.useMutation();

	// Track self-dispatched CustomEvents to avoid processing our own broadcasts
	const selfDispatchedRef = useRef<Set<string>>(new Set());

	// Listen for set-review-update CustomEvents from other components (e.g. SetReviewModal)
	// This ensures modal saves are reflected in the toggle immediately
	useEffect(() => {
		if (!user) return;

		const handleReviewEvent = (event: CustomEvent) => {
			const { userId: eventUserId, setId, review: eventReview } = event.detail;
			if (eventUserId !== user.id) return;

			// Skip events we dispatched ourselves (toggle already applied optimistic update)
			if (selfDispatchedRef.current.has(setId)) {
				selfDispatchedRef.current.delete(setId);
				return;
			}

			// Apply optimistic update from external source (modal save)
			applyOptimisticUpdate(setId, eventReview);

			// Clear the rapid-click ref so toggle reads from the fresh state
			optimisticStatusRef.current.delete(setId);

			// Also update schedule state
			if (eventReview) {
				setManagedSavedSchedule((prev) =>
					prev.includes(setId) ? prev : [...prev, setId],
				);
				setSavedSchedule((prev) =>
					prev.includes(setId) ? prev : [...prev, setId],
				);
			} else {
				setManagedSavedSchedule((prev) => prev.filter((id) => id !== setId));
				setSavedSchedule((prev) => prev.filter((id) => id !== setId));
			}
		};

		window.addEventListener(
			"set-review-update" as any,
			handleReviewEvent as any,
		);
		return () => {
			window.removeEventListener(
				"set-review-update" as any,
				handleReviewEvent as any,
			);
		};
	}, [user, applyOptimisticUpdate, setSavedSchedule, setManagedSavedSchedule]);

	// Debounce mutation per setId — fires only the final state after rapid clicks settle
	const pendingMutationRef = useRef<{ [setId: string]: NodeJS.Timeout }>({});

	// Track latest optimistic status per set — survives between re-renders so rapid
	// clicks always read the latest state even before React re-renders the closure.
	const optimisticStatusRef = useRef<Map<string, "none" | "want" | "must" | "sawLive" | "sawStream">>(new Map());

	// Handle toggle set in schedule - now uses useSaveSetReview for consistency
	const handleToggleSetInSchedule = useCallback(
		(setId: string) => {
			if (!user) {
				onAuthRequired("Sign in to save sets to your schedule");
				return;
			}

			if (!festivalId) {
				return;
			}

			const review = reviews.find((r) => r.setId === setId);
			const set = processedSets.find((s) => s.id === setId);

			// Read from ref first (always current even before re-render), fall back to reviews
			let currentState: "none" | "want" | "must" | "sawLive" | "sawStream" =
				optimisticStatusRef.current.get(setId) || "none";

			// Only derive from review if ref doesn't have it (first click or after reset)
			if (!optimisticStatusRef.current.has(setId) && review) {
				if (review.sawStream) currentState = "sawStream";
				else if (review.sawLive) currentState = "sawLive";
				else if (review.mustSee) currentState = "must";
				else if (review.wantToSee) currentState = "want";
			}

			// Cycle: none → want → must → sawLive → sawStream → none
			const nextState =
				currentState === "none"
					? "want"
					: currentState === "want"
						? "must"
						: currentState === "must"
							? "sawLive"
							: currentState === "sawLive"
								? "sawStream"
								: "none";

			// Update ref immediately — next click reads this before re-render
			if (nextState === "none") {
				optimisticStatusRef.current.delete(setId);
			} else {
				optimisticStatusRef.current.set(setId, nextState);
			}

			// Update schedule immediately for instant UI feedback
			if (nextState === "none") {
				// Remove from schedule
				setManagedSavedSchedule((prev) => prev.filter((id) => id !== setId));
				setSavedSchedule((prev) => prev.filter((id) => id !== setId));
			} else {
				// Add to schedule
				setManagedSavedSchedule((prev) =>
					prev.includes(setId) ? prev : [...prev, setId],
				);
				setSavedSchedule((prev) =>
					prev.includes(setId) ? prev : [...prev, setId],
				);
			}

			// Execute toggle using tRPC mutation directly
			const isDeleting = nextState === "none";

			// Build the optimistic review data
			const optimisticReview = isDeleting
				? null
				: {
						setId,
						artistId: set?.artist?.id || "",
						rating: review?.rating || 0,
						content: review?.content || null,
						loved: review?.loved || false,
						wantToSee: nextState === "want",
						mustSee: nextState === "must",
						sawLive: nextState === "sawLive",
						sawStream: nextState === "sawStream",
					};

			// Apply optimistic update to local state
			applyOptimisticUpdate(setId, optimisticReview);

			// Mark as self-dispatched so our listener skips it
			selfDispatchedRef.current.add(setId);

			// Broadcast optimistic update to all RealtimeSetCard instances
			const event = new CustomEvent("set-review-update", {
				detail: {
					userId: user.id,
					setId,
					artistId: set?.artist?.id || "",
					review: optimisticReview,
				},
			});
			window.dispatchEvent(event);

			// Debounce mutation — only send the final state after rapid clicks settle (300ms)
			// Each click cancels the previous pending mutation for this setId
			if (pendingMutationRef.current[setId]) {
				clearTimeout(pendingMutationRef.current[setId]);
			}

			const mutationData = {
				setId,
				artistId: set?.artist?.id || "",
				rating: isDeleting ? -2 : review?.rating || 0,
				content: isDeleting ? "" : review?.content || "",
				loved: isDeleting ? false : review?.loved || false,
				wantToSee: nextState === "want",
				mustSee: nextState === "must",
				sawLive: nextState === "sawLive",
				sawStream: nextState === "sawStream",
			};

			pendingMutationRef.current[setId] = setTimeout(() => {
				delete pendingMutationRef.current[setId];
				saveSetReviewMutation.mutate(mutationData);

				// Track analytics only for the final settled state
				if (festivalId && set?.artist?.name) {
					const analyticsAction =
						nextState === "sawLive"
							? "reviewed"
							: nextState === "must"
								? "must_see"
								: nextState === "want"
									? "want_to_see"
									: "schedule_removed";

					trackSetInteraction(
						festivalId,
						setId,
						analyticsAction,
						set.artist.name,
					);
				}
			}, 300);
		},
		[
			user,
			reviews,
			onAuthRequired,
			saveSetReviewMutation,
			festivalId,
			processedSets,
			setSavedSchedule,
			setManagedSavedSchedule,
			applyOptimisticUpdate,
		],
	);

	// Sync modal data with current review state (including optimistic updates)
	useEffect(() => {
		if (reviewingSet) {
			const currentReview = reviews.find((r) => r.setId === reviewingSet);
			const isOptDeleted = optimisticDeletes.includes(reviewingSet);

			const modalData = {
				rating: currentReview?.rating || 0,
				content: currentReview?.content || "",
				loved: currentReview?.loved || false,
				sawLive: currentReview?.sawLive || false,
				sawStream: currentReview?.sawStream || false,
				wantToSee: currentReview?.wantToSee || false,
				mustSee: currentReview?.mustSee || false,
			};
			setCurrentReviewData(modalData);
		}
	}, [reviews, reviewingSet, optimisticDeletes]);

	// Handle start review
	const handleStartReview = useCallback(
		(setId: string) => {
			// Basic validation - festival ID must be available
			if (!festivalId) {
				return;
			}

			// Check if social interactions are loaded before opening modal
			if (!socialInteractionsLoaded) {
				// Don't open modal if data isn't ready
				return;
			}

			// Force any open hover cards to close by blurring the active element
			if (document.activeElement instanceof HTMLElement) {
				document.activeElement.blur();
			}

			// Initialize review data from existing review or defaults
			// Use the current reviews state which includes optimistic updates
			const existingReview = reviews.find((r) => r.setId === setId);

			// When no review exists (deleted/cleared), all fields should be empty
			const modalData = {
				rating: existingReview?.rating || 0,
				content: existingReview?.content || "",
				loved: existingReview?.loved || false,
				sawLive: existingReview?.sawLive || false,
				sawStream: existingReview?.sawStream || false,
				wantToSee: existingReview?.wantToSee || false,
				mustSee: existingReview?.mustSee || false,
			};

			setCurrentReviewData(modalData);

			// Small delay to ensure hover cards have time to close
			setTimeout(() => {
				setReviewingSet(setId);
			}, 100);

			// For anonymous users, they'll see the SetAnonModal instead of SetReviewModal
			// This is handled in FestivalModalManager based on user authentication state
		},
		[reviews, socialInteractionsLoaded, festivalId, optimisticReviews],
	);

	// Handle close review modal
	const handleCloseReviewModal = useCallback(() => {
		setReviewingSet(null);
	}, []);

	// Handle review field changes
	const handleReviewChange = useCallback(
		(field: string, value: string | number | boolean) => {
			setCurrentReviewData((prev) => ({
				...prev,
				[field]: value,
			}));
		},
		[],
	);

	// Handle review save callback - called AFTER the modal has saved
	// The SetReviewModal handles its own saving via useRealtimeSetReview
	// This is just a notification callback to close the modal
	const handleReviewSave = useCallback(() => {
		// The modal has already saved the review
		// Just close the modal
		handleCloseReviewModal();

		// 4. REFETCH SERVER DATA for consistency (if available)
		if (refetchSetReviews) {
			setTimeout(() => {
				refetchSetReviews();
			}, 500); // Small delay to ensure server has processed the save
		}
	}, [
		reviewingSet,
		user,
		processedSets,
		currentReviewData,
		saveSetReviewMutation,
		handleCloseReviewModal,
		refetchSetReviews,
		festivalId,
		reviews,
		setSavedSchedule,
		setManagedSavedSchedule,
		applyOptimisticUpdate,
	]);

	return {
		// State
		reviews,
		reviewingSet,
		currentReviewData,
		managedSavedSchedule,

		// Actions
		handleToggleSetInSchedule,
		handleStartReview,
		handleCloseReviewModal,
		handleReviewChange,
		handleReviewSave,
	};
}
